import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingMessage {
  text: string;
  duration: number;
}

const loadingMessages: LoadingMessage[] = [
  {
    text: "Analyzing the key reasons for low conversion rates...",
    duration: 7000
  },
  {
    text: "Found {n} conversion killers. Searching for ways to improve them...",
    duration: 10000
  },
  {
    text: "An interesting fact while you wait: according to X research, 80% of websites lose potential revenue due to weak headlines that fail to capture customers' attention.",
    duration: 15000
  },
  {
    text: "AI selects the best solutions to address low conversion issues on your website...",
    duration: 12000
  },
  {
    text: "Just a little more, AI is finalizing the audit and preparing recommendations...",
    duration: 8000
  }
];

export function LoadingState() {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [counter, setCounter] = useState(0);
  const [isMessageChanging, setIsMessageChanging] = useState(false);
  const targetNumber = 17;

  useEffect(() => {
    let messageTimeout: NodeJS.Timeout;
    let nextMessage = 0;

    const scheduleNextMessage = () => {
      const currentDuration = loadingMessages[nextMessage].duration;
      
      messageTimeout = setTimeout(() => {
        setIsMessageChanging(true);
        
        setTimeout(() => {
          setCurrentMessage(nextMessage);
          setIsMessageChanging(false);
          nextMessage = (nextMessage + 1) % loadingMessages.length;
          if (nextMessage !== 0) scheduleNextMessage();
        }, 500);
      }, currentDuration);
    };

    scheduleNextMessage();

    const counterInterval = setInterval(() => {
      setCounter(prev => {
        if (prev < targetNumber) {
          return Math.min(prev + 1, targetNumber);
        }
        clearInterval(counterInterval);
        return prev;
      });
    }, 200);

    return () => {
      clearTimeout(messageTimeout);
      clearInterval(counterInterval);
    };
  }, []);

  const getCurrentMessage = () => {
    return loadingMessages[currentMessage].text.replace('{n}', counter.toString());
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-12 py-32 px-4 min-h-[60vh]">
      <div className="flex items-center gap-4">
        <div className="relative">
          <div className="absolute -inset-1 rounded-full bg-green-500/30 animate-pulse"></div>
          <Loader2 className="w-8 h-8 animate-spin text-green-500 relative" />
        </div>
        <span className="text-2xl font-medium bg-gradient-to-r from-black to-gray-600 bg-clip-text text-transparent">
          Analyzing your website...
        </span>
      </div>
      
      <div 
        className={`max-w-2xl text-center transition-all duration-500 ease-in-out transform ${
          isMessageChanging ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        }`}
      >
        <p className="text-lg text-gray-700 leading-relaxed">
          {getCurrentMessage()}
        </p>
      </div>

      <div className="w-full max-w-md space-y-3">
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-1000 ease-out"
            style={{ 
              width: `${(currentMessage / (loadingMessages.length - 1)) * 100}%`,
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
            }}
          />
        </div>
        <div className="flex justify-between text-sm text-gray-500">
          <span>Analyzing website</span>
          <span>{Math.round((currentMessage / (loadingMessages.length - 1)) * 100)}%</span>
        </div>
      </div>
    </div>
  );
}